import { Drawer } from "@base-ui/react/drawer";
import * as stylex from "@stylexjs/stylex";
import type { ReactElement, ReactNode } from "react";
import { CloseIcon } from "../icons.ts";
import "../tokens.stylex.ts";
import { color, fontSize, fontWeight, lineHeight, radius, shadow, space, zIndex } from "../tokens.stylex.ts";
import { Button } from "./Button.tsx";
import { isTransitioning } from "./overlay.styles.ts";
export interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the sheet. */
  trigger?: ReactElement;
  title: ReactNode;
  description?: ReactNode;
  /** Keep the title for assistive tech but don't show the header row. */
  hideHeader?: boolean;
  children?: ReactNode;
  /** Footer actions. Stacked full-width, the last one on top (put the primary action last). */
  footer?: ReactNode;
}

/**
 * Bottom sheet — the mobile counterpart of `Dialog`. Slides up from the bottom
 * edge, can be swiped down to dismiss, respects the home-indicator safe area
 * and keeps focused fields above the software keyboard.
 */
export function Sheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  hideHeader,
  children,
  footer
}: SheetProps) {
  return <Drawer.Root open={open} onOpenChange={onOpenChange ? next => onOpenChange(next) : undefined}>
      {trigger ? <Drawer.Trigger render={trigger} /> : null}
      <Drawer.VirtualKeyboardProvider>
        <Drawer.Portal>
          <Drawer.Backdrop className={state => ({
          0: {
            className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x6u05ig x19991ni xjmur4l x1be392t"
          },
          1: {
            className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni xjmur4l x1be392t xg01cxk"
          }
        })[!!isTransitioning(state.transitionStatus) << 0].className} />
          <Drawer.Viewport className="xixxii4 x10a8y8t xlj7wz5 x78zum5 xuk3077 xl56j7k x1kgghx x47corl">
            <Drawer.Popup className={state => ({
            0: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz xacbhai x11xpdln xjmur4l x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5"
            },
            4: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz xacbhai x11xpdln xjmur4l x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 x87ps6o"
            },
            2: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz x11xpdln xjmur4l x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 xwioj0c"
            },
            6: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz x11xpdln xjmur4l x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 x87ps6o xwioj0c"
            },
            1: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz xacbhai x11xpdln x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 xxey3e3"
            },
            5: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz xacbhai x11xpdln x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 x87ps6o xxey3e3"
            },
            3: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz x11xpdln x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 xwioj0c xxey3e3"
            },
            7: {
              className: "x1n2onr6 x78zum5 xdt5ytf xh8yej3 x11gisft xmz0i5r x67bb7w x1paulmi xmkeg23 x1qhh985 x1y0btm7 x4bnxre xtzlleu x34fko1 xf9tyuz x1a2a7pz x11xpdln x1be392t x1so62im x1s928wv x1j6awrg x1srwf7z x1yan0pp x1p8eww1 x177nr8b xsuwbf5 x87ps6o xwioj0c xxey3e3"
            }
          })[!!state.swiping << 2 | !!isTransitioning(state.transitionStatus) << 1 | !!(state.transitionStatus === "ending") << 0].className}>
              <div className="x2lah0s xamitd3 x14qfxbe x1ycjhwn x4wamar x257jf6 xroswyw" aria-hidden />
              <div {...{
              0: {
                className: "x2lah0s x78zum5 x1cy8zhl x1qughib xpcbtbh x2sz766 x1l9m7az x167c1dq x1iapb1r"
              },
              1: {
                className: "x2lah0s x78zum5 x1cy8zhl x1qughib xpcbtbh x2sz766 x1l9m7az x167c1dq x1iapb1r x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr x1hyvwdk xuxw1ft"
              }
            }[!!hideHeader << 0]}>
                <div className="x78zum5 xdt5ytf xjenv9e xeuugli x2sz766">
                  <Drawer.Title className="x1ghz6dp x1p23z6u x1ivwu1z x9aqtqa">{title}</Drawer.Title>
                  {description ? <Drawer.Description className="x1ghz6dp xluwven x187ovcu x9tub0y">{description}</Drawer.Description> : null}
                </div>
                <Drawer.Close render={<Button variant="ghost" iconOnly icon={<CloseIcon size={18} />} aria-label="Close" />} />
              </div>
              <Drawer.Content {...{
              0: {
                className: "x1iyjqo2 x2lwn1j x1odjw0f xish69e xsb4d8f x1q13efz"
              },
              1: {
                className: "x1iyjqo2 x2lwn1j x1odjw0f xish69e xsb4d8f x1f75lda"
              }
            }[!!!footer << 0]}>{children}</Drawer.Content>
              {footer ? <div className="x2lah0s x78zum5 x3ieub6 x1qjc9v5 x1a62zme xh6stid xsb4d8f x2wg95o x178xt8z x13fuv20 x8yrp0t xvccuex">{footer}</div> : null}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.VirtualKeyboardProvider>
    </Drawer.Root>;
}

// iOS-like sheet motion: a quick launch that settles softly.
const SHEET_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const SHEET_DURATION = "420ms";
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";